import { useEffect, useState, useRef } from 'react';
import type { DispatchAssignment, DispatchAssignmentEmployee, DispatchServiceArea, SaveApiRequest, DispatchAssignmentParams } from '../types/assignment.types';
import { AssignmentAPI } from '../api/assignment.api';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import snackBar from '../../../utilities/snackBar/SnackBar';

export function useAssignments(initialQuery: DispatchAssignmentParams) {
    const history = useHistory();

    const [assignmentsData, setAssignmentsData] = useState<DispatchAssignment[]>([]);
    const assignmentsRef = useRef<DispatchAssignment[]>([]);

    useEffect(() => {
        assignmentsRef.current = assignmentsData;
    }, [assignmentsData]);

    const [serviceAreasData, setServiceAreasData] = useState<DispatchServiceArea[]>([]);
    const [employeesData, setEmployeesData] = useState<DispatchAssignmentEmployee[]>([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);

    const [searchPeriod, setSearchPeriod] = useState<string>();
    const [searchServiceAreaId, setSearchServiceAreaId] = useState<string>();
    const [searchWeekStart, setSearchWeekStart] = useState<string>();

    const [showAssignmentModal, setShowAssignmentModal] = useState<boolean>(false);

    const [startDateField, setStartDateField] = useState<string>();
    const [endDateField, setEndDateField] = useState<string>();
    const [employeeIdField, setEmployeeIdField] = useState<Number>();
    const [rankField, setRankField] = useState<string>();

    const [showEditAssignmentModal, setShowEditAssignmentModal] = useState<boolean>(false);

    const [selectedEditAssignment, setSelectedEditAssignment] = useState<DispatchAssignment>();

    const [startDateEditField, setStartDateEditField] = useState<string>();
    const [endDateEditField, setEndDateEditField] = useState<string>();
    const [rankEditField, setRankEditField] = useState<string>();

    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState<boolean>(false);

    const [loadingCalendar, setLoadingCalendar] = useState<boolean>(false);

    const toggleLoadingCalendar = (loading: boolean) => {
        setLoadingCalendar(loading);
    }

    // Only run this initialization logic once, on first render.
    // `useRef` acts as a flag to remember if we've already run it.
    const isFirst = useRef(true);
    useEffect(() => {
        if (isFirst.current) {
            isFirst.current = false;

            const waitingGetAssignments = async (startDate: string, endDate: string, serviceAreaId: string) => {                
                await initialGetDispatchAssignments(startDate, endDate, serviceAreaId);
            }

            if (initialQuery.serviceAreaId && !isNaN(Number(initialQuery.serviceAreaId))) {
                setSearchServiceAreaId(initialQuery.serviceAreaId);
            }

            // If 'period' is present in url params, even if start date is also provided, we use this logic
            if (initialQuery.period) {
                let view = initialQuery.period;
                
                // if 'period' exists in params but does not match, default to 'current'
                if (view != 'current' && view != 'previous' && view != 'next') {
                    view = 'current';
                }

                // default to 'current' week
                let startDate: string = dayjs().day(0).format('YYYY-MM-DD');    // Sunday
                let endDate: string = dayjs().day(6).format('YYYY-MM-DD');      // Saturday

                if (view == 'previous') {
                    startDate = dayjs().day(0).subtract(7, 'day').format('YYYY-MM-DD');
                    endDate = dayjs().day(6).subtract(7, 'day').format('YYYY-MM-DD');                                       
                }

                if (view == 'next') {
                    startDate = dayjs().day(0).add(7, 'day').format('YYYY-MM-DD');
                    endDate = dayjs().day(6).add(7, 'day').format('YYYY-MM-DD');
                }

                setSearchPeriod(view);
                setSearchWeekStart(startDate);
                
                if (initialQuery.serviceAreaId && !isNaN(Number(initialQuery.serviceAreaId))) {
                    waitingGetAssignments(startDate, endDate, initialQuery.serviceAreaId);
                }

                return;
            }

            // Using 'dayjs' strict format
            if (dayjs(initialQuery.weekStart, 'YYYY-MM-DD', true).isValid()) {
                setSearchWeekStart(dayjs(initialQuery.weekStart).day(0).format('YYYY-MM-DD'));
            }

            if (dayjs(initialQuery.weekStart, 'YYYY-MM-DD', true).isValid()
            && initialQuery.serviceAreaId && !isNaN(Number(initialQuery.serviceAreaId))) {
                waitingGetAssignments(dayjs(initialQuery.weekStart).day(0).format('YYYY-MM-DD'), dayjs(initialQuery.weekStart).day(6).format('YYYY-MM-DD'), initialQuery.serviceAreaId);
            }            

            return;
        }
    }, [initialQuery]);

    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            setLoading(true);

            try {
                // Run both in parallel
                await Promise.all([getDispatchServiceAreas(), getDispatchAssignmentEmployees()]);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            isMounted = false;
        };
    }, []); 
    
    // date => 'YYYY-MM-DDTHH:mm:ss'
    const localToUtc = (date: string): string => {
        const local = dayjs(date);
        const utc = new Date(local.toDate().getTime() + local.toDate().getTimezoneOffset() * 60000);

        return dayjs(utc).format('YYYY-MM-DDTHH:mm:ss');
    }

    // date => 'YYYY-MM-DDTHH:mm:ss'
    const utcToLocal = (date: string): string => {
        const utc = dayjs(date);
        const local = new Date(utc.toDate().getTime() - utc.toDate().getTimezoneOffset() * 60000);

        return dayjs(local).format('YYYY-MM-DDTHH:mm:ss');
    }
    

    // This will force the date picked to be a sunday
    const updateStartDateParam = (date: string) => {                
        if (dayjs(date, 'YYYY-MM-DD', true).isValid()) {
            setSearchWeekStart(dayjs(date).day(0).format('YYYY-MM-DD'));
            setAssignmentsData([]);     // reset assignment data when date picker changes so user doesn't get confused about what they are seeing
        }
    }

    const updateServiceAreaIdParam = async (id: string) => {
        if (isNaN(Number(id))) {
            return;
        }
        
        setSearchServiceAreaId(id);
        setAssignmentsData([]);     // reset assignment data when date picker changes so user doesn't get confused about what they are seeing
    }

    const initialGetDispatchAssignments = async (startDate: string, endDate: string, serviceAreaId: string) => {
        try {
            if (startDate == null || endDate == null || serviceAreaId == null) {
                return;
            }

            const startDateUtc = dayjs(startDate).format('YYYY-MM-DDTHH:mm:ss');
            const endDateUtc = dayjs(endDate).format('YYYY-MM-DDTHH:mm:ss');

            const response = await AssignmentAPI.getDispatchAssignments(startDateUtc, endDateUtc, serviceAreaId);          

            const converted : DispatchAssignment[] = response.map((assignment) => ({
                ...assignment,
                start_date_local: dayjs(assignment.start_date_utc + 'Z').toDate(),
                end_date_local: dayjs(assignment.end_date_utc + 'Z').toDate()
            }));
            setAssignmentsData(converted);
        } catch (error) {
            if (error instanceof Error) {
                // setError(error.message);
                showError(error.message);
            } else {
                //setError('Unexpected Error');
                showError('Unexpected Error');
            }
        }
    }

    const getDispatchAssignments = async () => {
        try {
            if (searchWeekStart == null || searchServiceAreaId == null) {
                showError('Missing Required Fields');
                return;
            }

            const params = new URLSearchParams();
            params.set("weekStart", dayjs(searchWeekStart).day(0).format('YYYY-MM-DD'));
            params.set("serviceAreaId", searchServiceAreaId.toString());
            history.replace({ search: params.toString() });

            const startDateUtc = dayjs(searchWeekStart).day(0).format('YYYY-MM-DDTHH:mm:ss');
            const endDateUtc = dayjs(searchWeekStart).day(6).format('YYYY-MM-DDTHH:mm:ss');

            const response = await AssignmentAPI.getDispatchAssignments(startDateUtc, endDateUtc, searchServiceAreaId);

            const converted : DispatchAssignment[] = response.map((assignment) => ({
                ...assignment,
                start_date_local: dayjs(assignment.start_date_utc + 'Z').toDate(),
                end_date_local: dayjs(assignment.end_date_utc + 'Z').toDate()
            }));

            setAssignmentsData(converted);
        } catch (error) {
            if (error instanceof Error) {
                showError(error.message);
            } else {
                showError('Unexpected Error');
            }
        }
    }

    const getDispatchServiceAreas = async () => {
        try {
            const response = await AssignmentAPI.getDispatchServiceAreas();
            setServiceAreasData(response);
        } catch (error) {
            if (error instanceof Error) {
                showError(error.message);
            } else {
                showError('Unexpected Error');
            }
        }
    }

    const getDispatchAssignmentEmployees = async () => {
        try {
            const response = await AssignmentAPI.getDispatchAssignmentEmployees();
            setEmployeesData(response);
        } catch (error) {
            if (error instanceof Error) {
                showError(error.message);
            } else {
                showError('Unexpected Error');
            }
        }
    }

    const saveDispatchAssignments = async (dispatchAssignments: DispatchAssignment[]): Promise<boolean> => {
        try {
            const requestBody : SaveApiRequest[] = dispatchAssignments.map((assignment) =>  ({
                service_area_callout_assignment_id: assignment.service_area_callout_assignment_id ?? 0,
                employee_id: assignment.employee_id,
                service_area_id: assignment.service_area_id,
                profit_center_id: assignment.profit_center_id,
                start_date_utc: dayjs(assignment.start_date_utc).format('YYYY-MM-DDTHH:mm:ss'),
                end_date_utc: dayjs(assignment.end_date_utc).format('YYYY-MM-DDTHH:mm:ss'),
                assignment_rank: assignment.assignment_rank
            }));

            await AssignmentAPI.saveDispatchAssignments(requestBody);
            return true;
        } catch (error) {
            if (error instanceof Error) {
                // setError(error.message);
                showError(error.message);
                return false;
            } else {
                // setError('Unexpected Error');
                showError('Unexpected Error');
                return false;
            }
        }        
    }

    const createAssignment = async () => {
        if (!searchServiceAreaId) {
            showError('Select a service area');
            return;
        }

        if (!startDateField) {
            showError('Select a start date');
            return;
        }

        if (!endDateField) {
            showError('Select an end date');
            return;
        }

        if (!employeeIdField) {
            showError('Select an employee');
            return;
        }

        if (!rankField) {
            showError('Select an assignment rank');
            return;
        }

        const serviceArea = serviceAreasData.find(s => s.ServiceAreaID == Number(searchServiceAreaId));
        const employee = employeesData.find(e => e.EmployeeID == employeeIdField);

        if (!serviceArea || !employee) {
            showError('Unable to create assignment with selected employee OR service area');
            return;
        }

        const startOfDay = new Date(`${startDateField}T00:00:00`);
        const endOfDay = new Date(`${endDateField}T23:59:59`);

        const dispatchAssignment: DispatchAssignment[] = [{
            service_area_callout_assignment_id: 0,
            employee_id: employee.EmployeeID,
            service_area_id: serviceArea.ServiceAreaID,
            profit_center_id: serviceArea.ProfitCenterID!,  // TODO: verify if this is null, and if so what to do
            start_date_utc: new Date(startOfDay.getTime() + startOfDay.getTimezoneOffset() * 60000),
            end_date_utc: new Date(endOfDay.getTime() + endOfDay.getTimezoneOffset() * 60000),
            assignment_rank: Number(rankField)
        }]
        const success = await saveDispatchAssignments(dispatchAssignment);
        if (success) {
            clearStartDateField();
            clearEndDateField();
            clearEmployeeIdField();
            clearRankField();

            await getDispatchAssignments();

            toggleShowAssignmentModal(false);            
        }
    }

    const editAssignment = async () => {
        if (!selectedEditAssignment) {
            console.log('Selected schedule is invalid.');
            return;
        }

        if (!startDateEditField) {
            console.log('Start date is invalid.');
            return;
        }

        if (!endDateEditField) {
            console.log('End date is invalid.');
            return;
        }

        if (!rankEditField) {
            console.log('Assignment rank is invalid.');
            return;
        }

        const existStart = dayjs(selectedEditAssignment.start_date_local).format('YYYY-MM-DDTHH:mm:ss');
        const existEnd = dayjs(selectedEditAssignment.end_date_local).format('YYYY-MM-DDTHH:mm:ss');

        const newStart = dayjs(startDateEditField + 'T00:00:00').format('YYYY-MM-DDTHH:mm:ss');
        const newEnd = dayjs(endDateEditField + 'T23:59:59').format('YYYY-MM-DDTHH:mm:ss');

        const startDateModified: boolean = existStart != newStart;
        const endDateModified: boolean = existEnd != newEnd;

        const assignmentRankModified: boolean = selectedEditAssignment.assignment_rank.toString() != rankEditField

        if (!startDateModified && !endDateModified && !assignmentRankModified) {
            console.log('No changes were made.');
            return;
        }

        const startOfDay = new Date(`${startDateEditField}T00:00:00`);
        const endOfDay = new Date(`${endDateEditField}T23:59:59`);

        const dispatchAssignment: DispatchAssignment[] = [{
            service_area_callout_assignment_id: selectedEditAssignment.service_area_callout_assignment_id,
            employee_id: selectedEditAssignment.employee_id,
            service_area_id: selectedEditAssignment.service_area_id,
            profit_center_id: selectedEditAssignment.profit_center_id!,  // TODO: verify if this is null, and if so what to do
            start_date_utc: startDateModified ? new Date(startOfDay.getTime() + startOfDay.getTimezoneOffset() * 60000) : selectedEditAssignment.start_date_utc,
            end_date_utc: endDateModified ? new Date(endOfDay.getTime() + endOfDay.getTimezoneOffset() * 60000) : selectedEditAssignment.end_date_utc,
            assignment_rank: assignmentRankModified ? Number(rankEditField) : selectedEditAssignment.assignment_rank
        }]
        const success = await saveDispatchAssignments(dispatchAssignment);
        if (success) {
            clearStartDateEditField();
            clearEndDateEditField();
            clearRankEditField();
            clearSelectedEditAssignment();

            await getDispatchAssignments();

            toggleShowEditAssignmentsModal(false);            
        }

    }

    const deleteDispatchAssignments = async (serviceAreaCalloutAssignmentIDs: number[]): Promise<boolean> => {
        try {
            await AssignmentAPI.deleteDispatchAssignments(serviceAreaCalloutAssignmentIDs);
            return true;
        } catch (error) {
            if (error instanceof Error) {
                showError(error.message);
                return false;
            } else {
                showError('Unexpected Error');
                return false;
            }
        }
    }

    const deleteAssignment = async () => {
        if (!selectedEditAssignment) {
            console.log('Selected schedule is invalid.');
            return;
        }

        if (!selectedEditAssignment.service_area_callout_assignment_id) {
            console.log('Invalid ID');
            return;
        }

        const ids: number[] = [];
        ids.push(selectedEditAssignment.service_area_callout_assignment_id);

        const success: boolean = await deleteDispatchAssignments(ids);
        if (success) {
            clearStartDateEditField();
            clearEndDateEditField();
            clearRankEditField();
            clearSelectedEditAssignment();

            await getDispatchAssignments();

            toggleShowConfirmDeleteModal(false);
            toggleShowEditAssignmentsModal(false);            
        }
    }

    const navigatePrevious = async () => {        
        if (searchWeekStart == null) {
            console.log('Missing required date field');
            return;
        }

        const previousStartDate = dayjs(searchWeekStart).day(0).subtract(7, 'day').format('YYYY-MM-DD');        
        updateStartDateParam(previousStartDate);
        
        if (searchServiceAreaId && previousStartDate) {
            await initialGetDispatchAssignments(previousStartDate, dayjs(previousStartDate).day(6).format('YYYY-MM-DD'), searchServiceAreaId);
            
            const params = new URLSearchParams();
            params.set("weekStart", dayjs(previousStartDate).day(0).format('YYYY-MM-DD'));
            params.set("serviceAreaId", searchServiceAreaId.toString());
            history.replace({ search: params.toString() });
        }
    }

    const navigateNext = async ()  => {
        if (searchWeekStart == null) {
            console.log('Missing required date field');
            return;
        }

        const nextStartDate = dayjs(searchWeekStart).day(0).add(7, 'day').format('YYYY-MM-DD');
        updateStartDateParam(nextStartDate);

        if (searchServiceAreaId && nextStartDate) {            
            await initialGetDispatchAssignments(nextStartDate, dayjs(nextStartDate).day(6).format('YYYY-MM-DD'), searchServiceAreaId);

            const params = new URLSearchParams();
            params.set("weekStart", dayjs(nextStartDate).day(0).format('YYYY-MM-DD'));
            params.set("serviceAreaId", searchServiceAreaId.toString());
            history.replace({ search: params.toString() });
        }
    }

    const toggleShowAssignmentModal = (show: boolean) => {
        setShowAssignmentModal(show);
    }

    const updateStartDateField = (date: string) => {
        if (dayjs(date, 'YYYY-MM-DD', true).isValid()) {
            setStartDateField(dayjs(date).format('YYYY-MM-DD'));
        }
    }

    const clearStartDateField = () => {
        setStartDateField(undefined);
    }

    const updateEndDateField = (date: string) => {
        if (dayjs(date, 'YYYY-MM-DD', true).isValid()) {
            setEndDateField(dayjs(date).format('YYYY-MM-DD'));
        }
    }

    const clearEndDateField = () => {
        setEndDateField(undefined);
    }

    const updateEmployeeIdField = (id: string) => {
        if (!isNaN(Number(id))) {
            setEmployeeIdField(Number(id));
        }
    }

    const clearEmployeeIdField = () => {
        setEmployeeIdField(undefined);
    }

    const updateRankField = (rank: string) => {
        if (!isNaN(Number(rank))) {
            setRankField(rank);
        }
    }

    const clearRankField = () => {
        setRankField(undefined);
    }

    const showError = (message: string) => {
        snackBar({ text: message, type: 'error', timeout: 4000 });
    }

    const toggleShowEditAssignmentsModal = (show: boolean) => {
        setShowEditAssignmentModal(show);
    }

    const updateStartDateEditField = (date: string) => {
        if (dayjs(date, 'YYYY-MM-DD', true).isValid()) {
            setStartDateEditField(dayjs(date).format('YYYY-MM-DD'));
        }
    }

    const updateEndDateEditField = (date: string) => {
        if (dayjs(date, 'YYYY-MM-DD', true).isValid()) {
            setEndDateEditField(dayjs(date).format('YYYY-MM-DD'));
        }
    }

    const updateRankEditField = (rank: string) => {
        if (!isNaN(Number(rank))) {
            setRankEditField(rank);
        }
    }

    const updateSelectedEditAssignment = (assignment: DispatchAssignment) => {
        if (assignment) {
            setSelectedEditAssignment(assignment);
        }
    }

    const clearStartDateEditField = () => {
        setStartDateEditField(undefined);
    }
    const clearEndDateEditField = () => {
        setEndDateEditField(undefined);
    }
    const clearRankEditField = () => {
        setRankEditField(undefined);
    }
    const clearSelectedEditAssignment = () => {
        setSelectedEditAssignment(undefined);
    }

    const toggleShowConfirmDeleteModal = (show: boolean) => {
        setShowConfirmDeleteModal(show);
    }

    return { assignmentsData, serviceAreasData, employeesData, loading, error,
        getDispatchAssignments, getDispatchServiceAreas, getDispatchAssignmentEmployees,
        saveDispatchAssignments, deleteDispatchAssignments, 
        searchServiceAreaId, updateStartDateParam, searchWeekStart,
        updateServiceAreaIdParam, navigatePrevious, navigateNext, showAssignmentModal, toggleShowAssignmentModal,
        updateStartDateField, updateEndDateField, updateEmployeeIdField, startDateField, endDateField, employeeIdField,
        rankField, updateRankField, createAssignment, clearStartDateField, clearEndDateField, clearEmployeeIdField, clearRankField,
        showEditAssignmentModal, toggleShowEditAssignmentsModal,
        startDateEditField, endDateEditField, rankEditField,
        updateStartDateEditField, updateEndDateEditField, updateRankEditField, assignmentsRef,        
        selectedEditAssignment, updateSelectedEditAssignment, editAssignment, deleteAssignment,
        showConfirmDeleteModal, toggleShowConfirmDeleteModal, loadingCalendar, toggleLoadingCalendar
    };
}